import { useNavigate } from 'react-router-dom';
import type { ImportSession } from '@ni/shared';
import { useRetryImport } from '../../api/hooks';
import { IMPORT_RETRY_FILE, IMPORT_RETRY_FILE_WORKING, importRetryFileHint } from '../../lib/copy';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';

/**
 * **Start again on this file** (import note §4.2, 2026-09-14).
 *
 * A finished session refuses every door: the conversation is read-only, the plan
 * cannot be edited, the file cannot be replaced. That is right, and until now the
 * only way on was New import — find the export again, upload it again, and say
 * the whole thing to the planner from nothing. Lew's board of 2026-09-13 was
 * undone and then had to be re-explained.
 *
 * This is on the two surfaces somebody is actually standing on when they want
 * it: under the undo report on the Import tab, where a session was just taken
 * back, and under the read-only conversation, where the composer used to be and
 * is now simply gone.
 *
 * **Whether it is offered at all is decided here**, on the session, rather than
 * by each caller — two copies of one rule is the copy that drifts, and the rule
 * has two clauses:
 *
 *  - **the session has stopped.** Not `committed`, which is true from the press
 *    of Import: the structural pass and the notes reading both run under
 *    `in_progress`, where the server answers `IMPORT_STILL_EDITABLE` because
 *    that session is still a session and the thing to do with it is wait.
 *  - **and it holds an upload.** A `dossier:import` batch opens a session of its
 *    own so its packages are a unit of undo, and it ends `complete` with no
 *    `import_file` row behind it and no bytes to point a new session at. `files`
 *    is that history on the wire, and the server's own refusal
 *    (`IMPORT_HAS_NO_FILE`) reads the same row — so the button is absent rather
 *    than a 409 waiting to happen.
 *
 * **It navigates**, because the answer is a different session. The hook seeds
 * that session's cache with what came back, so the page it lands on is already
 * drawn.
 *
 * The hint is **under** the button and outside the action row, not beside it.
 * `.layer-actions` is a centered flex line, so a sentence in it is laid out as
 * one more control — stretched by whatever the row has left and broken wherever
 * it runs out. It is the same reason the run failure line sits outside
 * `.form-actions` rather than in it.
 */
export function RetryWithThisFile({ session }: { session: ImportSession }) {
  const retry = useRetryImport(session.id);
  const navigate = useNavigate();

  if (session.state === 'in_progress' || session.files.length === 0) return null;

  return (
    <div className="retry-file">
      <div className="layer-actions">
        <WorkingButton
          label={IMPORT_RETRY_FILE}
          workingLabel={IMPORT_RETRY_FILE_WORKING}
          variant="act"
          working={retry.isPending}
          onClick={() =>
            retry.mutate(undefined, {
              onSuccess: (started: ImportSession) => navigate(`/imports/${started.id}`),
            })
          }
        />
      </div>
      {/* What carries, said as it is rather than as it usually is: a session
          that never settled a plan has no plan to promise. */}
      <div className="src">{importRetryFileHint(session.plan_version !== null)}</div>
      {retry.isError ? <ErrorLine error={retry.error} /> : null}
    </div>
  );
}
